import { ScrollableModal } from '@/components/widgets/scrollable-modal';
import { useObservableState } from '@nf/utils-common/hooks';
import {  Icon } from '@/components/icons/svg-icons';
import { InputField, inputFieldStyles, InputFieldWrapper } from '@/jupiter/components/input-field';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Button, Dropdown } from '@nf/ui';
import { jupiterButton } from 'styled-system/recipes';
import { forgetPasswordStyles } from './forget-password.style';
import { forgetPasswordService } from '@/services/forget-password.service';
import { useForgetPassword } from '@/hooks/query/foreget-password/use-forget-password';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgetPasswordSchema } from '@/lib/schemas/forget-password.schema';
import { useTranslations } from 'next-intl';
import { modalService } from '@/services/modal.service';

interface FormInput {
	email: string;
	answer: string;
	question: string;
}

export interface ErrorMsg {
	emailEmpty: string;
	emailFormat: string;
	questionEmpty: string;
	answerEmpty: string;
}

export const ForgetPassword = () => {
	const { isShowForgetPasswordModal, closeModal } = useForgetPasswordModal();
	const [errorMessage, setErrorMessage] = useState('');
	const [isLogining, setIsLoading] = useState(false);
	const t = useTranslations('General');

	const schema = forgetPasswordSchema({
		emailEmpty: t('lbl_EmptyField'),
		emailFormat: t('lbl_WrongFormatField'),
		questionEmpty: t('lbl_EmptyField'),
		answerEmpty: t('lbl_EmptyField'),
	});
	const {
		control, 
		register,
		handleSubmit,
		clearErrors,
		formState: { errors },
		setValue,
	} = useForm<FormInput>({ mode: 'onBlur', resolver: zodResolver(schema), defaultValues: {
		email: '',  
		answer: '',
		question: '',
	} });
	const { securityQuestions, changeForgetPassword  } = useForgetPassword(isShowForgetPasswordModal);

	const initForgetPasswordModal = () => {
		setValue('email', '', { shouldValidate: false });
		setValue('question', '', { shouldValidate: false });
		setValue('answer', '', { shouldValidate: false });
		clearErrors();
		setErrorMessage('');
	}

	const submit: SubmitHandler<FormInput> = async ({ email, answer, question }) => {
		try {
			setErrorMessage('');
			setIsLoading(true);
			const isSuccess = await changeForgetPassword({ email, SecurityAnswer: answer, SecurityQuestion: question });
			if (isSuccess) {
				modalService.openJupiterMessageModal({
					title: t('lbl_SuccessfulTitle'),
					message: t('lbl_forgetPassSuccess'),
				});
				closeModal();
			}

		} catch (e: any) {
			if (e?.ErrorMsg) {
				setErrorMessage(e?.ErrorMsg);
			}
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		initForgetPasswordModal();
	}, [isShowForgetPasswordModal])

	return (
		securityQuestions.length> 0 &&
		<ScrollableModal
			sectionName={forgetPasswordStyles.section}
			className={forgetPasswordStyles.modal}
			isOpen={isShowForgetPasswordModal}
			closeModal={closeModal}
			header={
				<header className={forgetPasswordStyles.header}>
					<h1 className={forgetPasswordStyles.headerTitle}>{t('lbl_forgetPaswordTitle')}</h1>
					<div className={forgetPasswordStyles.iconClose} onClick={closeModal}>
						<Icon name="close-big" />
					</div>
				</header>
			}
		>
			<main className={forgetPasswordStyles.root}>
				<div className={clsx(forgetPasswordStyles.content, 'jupiter-scrollbar')}>
					<div className={forgetPasswordStyles.formContainer}>
						<div className={forgetPasswordStyles.customText}>
							<span>
								{t('lbl_forgetPassDescription')}
							</span>
						</div>
						<form className={forgetPasswordStyles.formSection}>
							<div className={forgetPasswordStyles.inputGroup}>
								<div>
									<InputField
										label={t('lbl_forgetPassMail')}
										type="text"
										id="email"
										isError={!!errors.email}
										placeholder={t('lbl_forgetPassMail')}
										hint={errors.email && errors.email.message}
										required
										{...register('email', { required: true })}
									/>
								</div>
								<div>
									<InputFieldWrapper
										label={t('lbl_RegistQuestionEmpty')}
										required
										isError={!!errors.question}
										hint={errors.question && errors.question.message}
									>
										<Controller
											name="question"
											control={control}
											render={({ field: { onChange, value } }) => (
												<Dropdown 
													className={clsx(inputFieldStyles.inputField, forgetPasswordStyles.dropdown)}
													variant="right"
													value={value}
													options={securityQuestions!}
													onChange={(e) => {
														onChange(e.option.value)
													}}
													valueTemplate={() => (
														<>
															{securityQuestions.find(x => x.value === value)?.label}
														</>
													)}
													iconTemplate={() => <Icon name="arrow-down" />}
												/>
											)}
										/>
									</InputFieldWrapper>
								</div>
								<div>
									<InputField
										label={t('lbl_RegistAnswerEmpty')} 
										type="text"
										id="answer"
										isError={!!errors.answer}
										placeholder={t('lbl_RegistAnswerEmpty')}
										hint={errors.answer && errors.answer.message}
										required
										{...register('answer', { required: true })}
									/>
								</div>
							</div>
						</form>
						{errorMessage ? 
							<div className={forgetPasswordStyles.reqErrorText}>{errorMessage}</div>: null}
						<div className={forgetPasswordStyles.buttonContainer}>
							<Button 
								className={clsx(
									jupiterButton({ size: 'lg', visual: isLogining?'disable':'primary' }), 
									forgetPasswordStyles.loginButton
								)} 
								onClick={() => {
									setErrorMessage;
									handleSubmit(submit)();
								}}
							>
								{isLogining?(<Icon className={forgetPasswordStyles.loadingIcon} name="loading" />):t('lbl_SubmitBtn')}
							
							</Button>
							<button type="submit" className={forgetPasswordStyles.signUpButton}>
								<span onClick={closeModal} className={forgetPasswordStyles.buttonText}>{t('lbl_login')}</span>
							</button>
						</div>
					</div>
				</div>
			</main>
		</ScrollableModal>
	)
}

const useForgetPasswordModal = () => {
	const isShowForgetPasswordModal = useObservableState(forgetPasswordService.forgetPasswordModal$, false);
	const closeModal = () => {
		forgetPasswordService.displayForgetPasswordModal(false);
	}

	return {
		isShowForgetPasswordModal,
		closeModal
	}
}

